






































































































































































// core
import { AxiosError } from 'axios';
import { mapActions, mapGetters } from 'vuex';

// models
import {
  EmployeeType,
  EmployeeHourlySubType,
  IEmployeeData,
  EmployeeSalarySubType,
  EmployeeFieldError,
  IEmployeeWorkScheduleData,
  EmployeeWorkScheduleFieldError,
  IEmployeeTimeOffData,
  IEmployeeTimeOffEntitlement,
  EmployeeTimeOffFieldError
} from '@/models';

import { ALForm } from '@/common/component.types';

import { ErrorManager } from '@/models/error';

//components
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import DragDropFileInput from '@/components/common/DragDropFileInput.vue';
import EmployeeDetailFormCard from './EmployeeDetailFormCard.vue';

// utils
import mixins from '@/utils/mixins';

//mixins
import FormRules from '@/mixins/file-upload-rules';
import EmployeeScheduleDetailFormCard from './EmployeeScheduleDetailFormCard.vue';
import ALDialog from '../core/Dialog/ALDialog.vue';
import EmployeeTimeOffDetailFormCard from '../employee-time-off/EmployeeTimeOffDetailFormCard.vue';
import { InputValidationRule } from 'vuetify';
import { cloneDeep } from 'lodash';
import Vue from 'vue';

const baseMixins = mixins(FormRules);

interface Options extends InstanceType<typeof FormRules> {
  $refs: {
    form: ALForm;
    employeeDetailFormCard: InstanceType<typeof EmployeeDetailFormCard>;
    employeeScheduleDetailFormCard: InstanceType<
      typeof EmployeeScheduleDetailFormCard
    >;
    employeeTimeOffDetailFormCard: InstanceType<
      typeof EmployeeTimeOffDetailFormCard
    >;
  };
}

enum WizardStep {
  Detail = 1, // = 1,
  Schedule, // = 2,
  Timeoff // = 3
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    name: 'EmployeeDetailForm',

    components: {
      ErrorAlert,
      DragDropFileInput,
      EmployeeDetailFormCard,
      EmployeeScheduleDetailFormCard,
      EmployeeTimeOffDetailFormCard,
      ALDialog
    },

    data() {
      return {
        dialog: false,
        loading: false,

        employee: {} as IEmployeeData,
        employeeWorkSchedule: {
          scheduled_days: [],
          workday_hours: 0
        } as IEmployeeWorkScheduleData,
        employeeTimeOff: {} as IEmployeeTimeOffData,

        employeeTimeOffEntitlements: {} as Record<
          number,
          IEmployeeTimeOffEntitlement[]
        >,
        entitlementYearsModified: [] as number[],

        errorMessage: '',
        errorMessageDetail: '',
        employeeDetailFieldError: new EmployeeFieldError(),
        employeeWorkScheduleFieldError: new EmployeeWorkScheduleFieldError(),
        employeeTimeOffFieldError: new EmployeeTimeOffFieldError(),

        employeeFormValid: false,

        employeeTypes: [
          {
            display_name: 'Hourly',
            value: EmployeeType.Hourly
          },
          {
            display_name: 'Salary',
            value: EmployeeType.Salary
          }
        ],

        WizardStep,
        wizardStep: WizardStep.Detail
      };
    },

    computed: {
      ...mapGetters({
        getEmployeeById: 'employee/getEmployeeById',
        getEmployeeTimeOffEntitlements:
          'employeeTimeOff/getEmployeeTimeOffEntitlements',
        getEmployeeWorkSchedule: 'employee/getEmployeeWorkSchedule',
        getEmployeeTimeOff: 'employeeTimeOff/getEmployeeTimeOff'
      }),

      employeeIsHourly(): boolean {
        return this.employee.type == EmployeeType.Hourly;
      },

      wizardStepRules(): InputValidationRule {
        return () => {
          if (this.wizardStep == WizardStep.Detail) {
            return true;
          }
          return !!this.employee.id;
        };
      }
    },

    methods: {
      ...mapActions({
        uploadHourlyEmployees: 'employee/uploadHourlyEmployees',

        fetchEmployeeTimeOff: 'employeeTimeOff/fetchEmployeeTimeOff',
        fetchEmployeeTimeOffEntitlements:
          'employeeTimeOff/fetchEmployeeTimeOffEntitlements',
        fetchEmployeeWorkSchedule: 'employee/fetchEmployeeWorkSchedule'
      }),

      open(type: EmployeeType) {
        // in case of creating an employee
        this.employee = {
          type: type
        } as IEmployeeData;

        if (type == EmployeeType.Hourly) {
          this.employee.subtype = EmployeeHourlySubType.DayShift;
        } else if (type == EmployeeType.Salary) {
          this.employee.subtype = EmployeeSalarySubType.Salary;
        }

        this.clearAllErrors();

        // reset tab to form
        this.wizardStep = WizardStep.Detail;

        this.dialog = true;

        this.resetFormValidation();
      },

      clearAllErrors(): void {
        this.errorMessage = '';
        this.errorMessageDetail = '';

        this.employeeDetailFieldError.clearErrors();
        this.employeeWorkScheduleFieldError.clearErrors();
        this.employeeTimeOffFieldError.clearErrors();
      },

      close() {
        this.clearAllErrors();
        this.dialog = false;
      },

      saveCurrentForm() {
        if (this.validateForm()) {
          switch (this.wizardStep) {
            case WizardStep.Detail:
              return this.saveEmployee();
            case WizardStep.Schedule:
              return this.saveEmployeeWorkSchedule();
            case WizardStep.Timeoff:
              return this.saveEmployeeTimeoff();
          }
        }
      },

      resetFormValidation(): void {
        if (this.$refs.form) {
          return this.$refs.form.resetValidation();
        }
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      async saveEmployee() {
        this.loading = true;

        const employeeDetailFormCard = this.$refs.employeeDetailFormCard;

        if (employeeDetailFormCard) {
          try {
            const employeeId = await employeeDetailFormCard.saveEmployee();
            // if no id is returned then we are just updating.
            this.employee = this.getEmployeeById(
              employeeId ?? this.employee.id
            );

            // Get latest work schedule.
            await this.fetchCurrentEmployeeWorkSchedule(this.employee.id!);

            // assigning this value updates the getter getEmployeeWorkSchedule directly.
            this.employeeWorkSchedule = cloneDeep(this.getEmployeeWorkSchedule);

            this.moveToWorkScheduleStep();
          } catch (err) {
            const error = err as AxiosError;
            if (error.response) {
              if (error.response.data.detail) {
                this.errorMessageDetail = error.response.data.detail;
              }
              if (
                EmployeeFieldError.isEmployeeFieldError(error.response.data)
              ) {
                this.employeeDetailFieldError = new EmployeeFieldError(
                  error.response.data
                );
              }
            }
            this.errorMessage = ErrorManager.extractApiError(error);
          } finally {
            this.loading = false;
          }
        }
      },

      async saveEmployeeWorkSchedule() {
        this.loading = true;
        const employeeScheduleDetailFormCard = this.$refs
          .employeeScheduleDetailFormCard;

        if (employeeScheduleDetailFormCard) {
          try {
            await employeeScheduleDetailFormCard.saveEmployeeWorkSchedule();

            await this.fetchCurrentEmployeeTimeOff(this.employee.id!);

            // not to update the getters directly by reference
            this.employeeTimeOff = cloneDeep(this.getEmployeeTimeOff);

            this.moveToTimeoffStep();
          } catch (err) {
            const error = err as AxiosError;
            this.errorMessage = ErrorManager.extractApiError(error);

            if (error.response) {
              if (error.response.data.detail) {
                this.errorMessageDetail = error.response.data.detail;
              }
              if (
                EmployeeWorkScheduleFieldError.isEmployeeWorkScheduleFieldError(
                  error.response.data
                )
              ) {
                this.employeeWorkScheduleFieldError = new EmployeeWorkScheduleFieldError(
                  error.response.data
                );
              }
            }
          } finally {
            this.loading = false;
          }
        }
      },

      async saveEmployeeTimeoff() {
        this.loading = true;
        const employeeTimeOffDetailFormCard = this.$refs
          .employeeTimeOffDetailFormCard;

        if (employeeTimeOffDetailFormCard) {
          try {
            await employeeTimeOffDetailFormCard.saveEmployeeTimeOff();
            this.close();
          } catch (err) {
            const error = err as AxiosError;
            this.errorMessage = ErrorManager.extractApiError(error);

            if (error.response) {
              if (error.response.data.detail) {
                this.errorMessageDetail = error.response.data.detail;
              }
              if (
                Array.isArray(error.response.data) &&
                EmployeeTimeOffFieldError.isEmployeeTimeOffFieldError({
                  entitlements: error.response.data
                })
              ) {
                this.employeeTimeOffFieldError = new EmployeeTimeOffFieldError({
                  entitlements: error.response.data
                });
              } else if (
                EmployeeTimeOffFieldError.isEmployeeTimeOffFieldError(
                  error.response.data
                )
              ) {
                this.employeeTimeOffFieldError = new EmployeeTimeOffFieldError(
                  error.response.data
                );
              }
            }
          } finally {
            this.loading = false;
          }
        }
      },

      async fetchCurrentEmployeeWorkSchedule(employee: number): Promise<void> {
        await this.fetchEmployeeWorkSchedule(employee).catch(error => {
          this.errorMessage = ErrorManager.extractApiError(error);
        });
      },

      async fetchCurrentEmployeeTimeOff(employee: number): Promise<void> {
        await this.fetchEmployeeTimeOff(employee).catch(error => {
          this.errorMessage = ErrorManager.extractApiError(error);
        });
      },

      async searchEmployeeTimeOffEntitlements(year: number, employee: number) {
        return this.fetchEmployeeTimeOffEntitlements({
          year,
          employee: employee
        })
          .then(() => {
            // remove copying by reference
            Vue.set(
              this.employeeTimeOffEntitlements,
              year,
              this.getEmployeeTimeOffEntitlements
            );
          })
          .catch(error => {
            this.errorMessage = ErrorManager.extractApiError(error);
          });
      },

      moveToDetailStep() {
        this.wizardStep = WizardStep.Detail;
      },

      moveToWorkScheduleStep() {
        this.wizardStep = WizardStep.Schedule;
      },

      moveToTimeoffStep() {
        this.wizardStep = WizardStep.Timeoff;
      }
    }
  });
